import type { ProductKeys3P } from '@atlassian/rovo-connectors-config/types';

import type { CloudConfig } from '../../../../../common/constants/filters/cloud-config/types';
import type { CreatedSelectFilter } from '../../../../../common/constants/filters/select-filter/types';
import { ProductKeys } from '../../../../../common/constants/product-keys';
import type { SelectFilterOption } from '../../../../../common/ui/filters/select-filter';
import { getProducts3PFromConfigForFilter } from '../../../../../common/utils/filters';
import { createMultiSelectFilter } from '../../../../filters/multi-select-filter';
import { getCachedThirdPartyFilterOptions } from '../utils/third-party-filter-options-cache';

const salesforceProducts: ProductKeys[] = [ProductKeys.Salesforce];
const products3PFromConfig: ProductKeys3P[] = getProducts3PFromConfigForFilter('campaignStatus');
const campaignStatusProducts = Array.from(
	new Set([...salesforceProducts, ...products3PFromConfig]),
);

const fetchDynamicCampaignStatusOptions = async (
	config: CloudConfig,
): Promise<SelectFilterOption[]> => {
	const cloudId = config.cloudId;
	const datasourceId = config.selectedDatasource?.datasourceId;

	if (!cloudId || !datasourceId) {
		return [];
	}

	try {
		const result = await getCachedThirdPartyFilterOptions(config);

		if (!result) {
			return [];
		}

		const campaignStatuses = result.filters.find((f) => f.name === 'campaignStatuses');
		if (!campaignStatuses || campaignStatuses.values.length === 0) {
			return [];
		}

		return campaignStatuses.values.map((status) => ({
			value: status.name,
			trackingKey: status.name,
			queryParamValue: status.name,
			isDefault: false,
			label: status.label ?? status.name,
			product: config.product,
			isSelected: false,
			isSuggested: false,
		}));
	} catch {
		return [];
	}
};

const fetchQueryParams = async (
	queryParams: string[],
	config: CloudConfig,
): Promise<SelectFilterOption[]> => {
	const allOptions = await fetchDynamicCampaignStatusOptions(config);
	const selected = new Set(queryParams);
	return allOptions.filter((option) => selected.has(option.queryParamValue));
};

export const filter: CreatedSelectFilter<'campaignStatus'> = createMultiSelectFilter({
	id: 'campaignStatus',
	requiresTopLevel3PDatasource: true,
	products: campaignStatusProducts,
	alwaysVisible: false,
	universal: false,
	queryParams: {
		key: 'campaignStatuses',
		fetcher: async (queryParams, config) => {
			return fetchQueryParams(queryParams, config);
		},
	},
	initialOptions: {
		fetcher: async (config) => {
			return fetchDynamicCampaignStatusOptions(config);
		},
	},
});
